import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, Plus, Trash2, LogOut, ClipboardList } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { AdminBookings } from "@/components/AdminBookings";
import { AdminEnquiries } from "@/components/AdminEnquiries";
import { AdminDataTab } from "@/components/AdminDataTab";
import { usePageMeta } from "@/hooks/usePageMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORE_KEY = "usdd_admin_pw";

export default function AdminPage() {
  usePageMeta("Admin", null);
  const [pw, setPw] = useState("");
  const [authed, setAuthed] = useState(false);
  const [tab, setTab] = useState("bookings");
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", excerpt: "", body: "" });

  const tryLogin = useCallback(async (password) => {
    try {
      await axios.post(`${API}/admin/verify`, {}, { headers: { "x-admin-password": password } });
      setPw(password);
      setAuthed(true);
      // sessionStorage > localStorage — clears on tab close, smaller XSS surface
      sessionStorage.setItem(STORE_KEY, password);
    } catch {
      sessionStorage.removeItem(STORE_KEY);
      toast.error("Invalid password");
    }
  }, []);

  useEffect(() => {
    const saved = sessionStorage.getItem(STORE_KEY);
    if (saved) tryLogin(saved);
  }, [tryLogin]);

  const loadItems = async (which, password = pw) => {
    const url = which === "testimonials" ? "/testimonials" : which === "posts" ? "/posts" : "/events";
    try {
      const r = await axios.get(`${API}${url}`);
      setItems(r.data);
    } catch { toast.error("Failed to load"); }
    void password;
  };

  const switchTab = (t) => {
    setTab(t);
    setItems([]);
    setForm({ title: "", excerpt: "", body: "" });
    // Bookings + Enquiries + Contacts + Policy Acks manage their own data.
    // Testimonials tab gets BOTH: the create/delete CRUD AND the CSV export
    // is exposed under "testimonials-export" so it stays out of the way.
    if (["bookings", "enquiries", "contacts", "policy-acks", "testimonials-export"].includes(t)) return;
    loadItems(t);
  };

  const create = async () => {
    if (tab === "testimonials") {
      if (!form.text || !form.name) return toast.error("text + name required");
      await axios.post(`${API}/testimonials`,
        { text: form.text, name: form.name, role: form.role || null },
        { headers: { "x-admin-password": pw } });
    } else if (tab === "posts") {
      if (!form.title || !form.body) return toast.error("title + body required");
      await axios.post(`${API}/posts`,
        { title: form.title, excerpt: form.excerpt || form.title, body: form.body },
        { headers: { "x-admin-password": pw } });
    } else {
      if (!form.title || !form.body) return toast.error("title + body required");
      await axios.post(`${API}/events`,
        { title: form.title, excerpt: form.excerpt || form.title, body: form.body, venue: form.venue || null, cta_label: "Register" },
        { headers: { "x-admin-password": pw } });
    }
    toast.success("Created");
    setForm({ title: "", excerpt: "", body: "" });
    loadItems(tab);
  };

  const del = async (item) => {
    try {
      if (tab === "testimonials") {
        await axios.delete(`${API}/testimonials/${item.id}`, { headers: { "x-admin-password": pw } });
      } else if (tab === "posts") {
        await axios.delete(`${API}/posts/${item.slug}`, { headers: { "x-admin-password": pw } });
      } else {
        await axios.delete(`${API}/events/${item.slug}`, { headers: { "x-admin-password": pw } });
      }
      toast.success("Deleted");
      loadItems(tab);
    } catch { toast.error("Delete failed"); }
  };

  if (!authed) {
    return (
      <>
        <Header />
        <main className="min-h-screen flex items-center justify-center bg-black pt-24 pb-16">
          <div className="max-w-md w-full mx-auto px-6">
            <div className="bg-zinc-950/80 border border-white/10 rounded-3xl p-8">
              <Lock size={32} className="text-brand-yellow mb-4" />
              <h1 className="font-display text-3xl text-white tracking-wide">Admin</h1>
              <p className="text-sm text-zinc-400 mt-1">Enter admin password.</p>
              <form
                onSubmit={(e) => { e.preventDefault(); tryLogin(pw); }}
                className="mt-6 flex flex-col gap-3"
              >
                <input
                  type="password"
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  data-testid="admin-password-input"
                  className="w-full bg-white/5 border border-white/15 rounded-xl px-4 py-3 text-white outline-none focus:border-brand-yellow"
                  placeholder="password"
                  autoFocus
                />
                <button data-testid="admin-signin-btn" className="px-5 py-3 rounded-full bg-brand-yellow text-black font-black uppercase text-sm tracking-wider hover:bg-brand-yellowHover transition-all">
                  Sign in
                </button>
              </form>
            </div>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <main className="min-h-screen bg-black pt-28 pb-20">
        <div className="max-w-5xl mx-auto px-6 sm:px-8">
          <div className="flex items-center justify-between mb-8">
            <h1 className="font-display text-4xl text-white tracking-wide">Admin</h1>
            <button
              onClick={() => { sessionStorage.removeItem(STORE_KEY); setAuthed(false); setPw(""); }}
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-zinc-400 hover:text-white"
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>

          <div className="flex gap-2 mb-8 flex-wrap">
            {["bookings", "enquiries", "contacts", "policy-acks", "testimonials-export", "events", "posts", "testimonials"].map((t) => (
              <button
                key={t}
                onClick={() => switchTab(t)}
                data-testid={`admin-tab-${t}`}
                className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-[0.2em] transition-all inline-flex items-center gap-1.5 ${
                  tab === t ? "bg-brand-yellow text-black" : "bg-white/5 border border-white/10 text-zinc-300 hover:border-white/30"
                }`}
              >
                {t === "bookings" && <ClipboardList size={12} />}
                {t === "policy-acks" ? "Policy Acks" : t === "testimonials-export" ? "Testimonials CSV" : t}
              </button>
            ))}
          </div>

          {tab === "bookings" ? (
            <AdminBookings pw={pw} />
          ) : tab === "enquiries" ? (
            <AdminEnquiries pw={pw} />
          ) : tab === "contacts" ? (
            <AdminDataTab pw={pw} kind="contacts" />
          ) : tab === "policy-acks" ? (
            <AdminDataTab pw={pw} kind="policy-acks" />
          ) : tab === "testimonials-export" ? (
            <AdminDataTab pw={pw} kind="testimonials" />
          ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
              <h2 className="font-display text-2xl text-white mb-4">Create</h2>
              <div className="space-y-3 bg-white/[0.02] border border-white/10 p-5 rounded-2xl">
                {tab === "testimonials" ? (
                  <>
                    <input className="ad-input" placeholder="Quote text" value={form.text || ""} onChange={(e) => setForm({ ...form, text: e.target.value })} />
                    <input className="ad-input" placeholder="Name" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                    <input className="ad-input" placeholder="Role (optional)" value={form.role || ""} onChange={(e) => setForm({ ...form, role: e.target.value })} />
                  </>
                ) : (
                  <>
                    <input className="ad-input" placeholder="Title" value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} />
                    <input className="ad-input" placeholder="Excerpt" value={form.excerpt || ""} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} />
                    {tab === "events" && (
                      <input className="ad-input" placeholder="Venue (optional)" value={form.venue || ""} onChange={(e) => setForm({ ...form, venue: e.target.value })} />
                    )}
                    <textarea className="ad-input min-h-[140px]" placeholder="Body" value={form.body || ""} onChange={(e) => setForm({ ...form, body: e.target.value })} />
                  </>
                )}
                <button onClick={create} data-testid="admin-create-btn" className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-brand-yellow text-black font-black uppercase text-xs tracking-wider hover:bg-brand-yellowHover transition-all">
                  <Plus size={14} /> Create
                </button>
              </div>
            </div>

            <div>
              <h2 className="font-display text-2xl text-white mb-4">Existing</h2>
              <div className="space-y-2">
                {items.map((it) => (
                  <div key={it.id} className="flex items-center justify-between gap-3 p-4 rounded-xl bg-white/[0.03] border border-white/10">
                    <div className="min-w-0">
                      <div className="text-sm text-white font-bold truncate">{it.title || it.name}</div>
                      <div className="text-[11px] text-zinc-500 truncate">{it.slug || it.role || it.text?.slice(0, 60)}</div>
                    </div>
                    <button onClick={() => del(it)} className="text-zinc-500 hover:text-red-400" aria-label="Delete">
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
                {items.length === 0 && <p className="text-zinc-500 text-sm">No items.</p>}
              </div>
            </div>
          </div>
          )}
        </div>

        <style>{`
          .ad-input { width:100%; background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.1); border-radius: 12px; padding: 12px 14px; color: white; font-size: 14px; outline: none; }
          .ad-input:focus { border-color: #FFDA1F; box-shadow: 0 0 0 3px rgba(255,218,31,0.15); }
          .ad-input::placeholder { color: rgba(255,255,255,0.35); }
        `}</style>
      </main>
      <Footer />
    </>
  );
}
